import React from 'react';
import { Link } from 'react-router-dom';

import RegisterForm from '../../components/auth/RegisterForm';
import LogoCarousel from '../../components/shared/LogoCarousel';

/**
 * @page Register
 * @description Página de registro para compra de pulseras SOS NFC y gestión de perfiles
 * @vibe-color 🔵 #3B82F6
 * @vibe-icon 📝
 */
const Register: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        {/* Panel lateral con información y carrusel */}
        <div className="hidden lg:block relative w-1/2">
          <div className="absolute inset-0 h-full w-full bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
            <div className="flex h-full flex-col items-center justify-center p-10 text-center">
              {/* Logo NFCores principal */}
              <div className="mb-8">
                <Link to="/" className="flex items-center justify-center space-x-3">
                  <img
                    className="h-16 w-auto"
                    src="/logo.svg"
                    alt="NFCores Logo"
                    onError={(e) => {
                      e.currentTarget.src = '/assets/images/NFCores-original.png';
                    }}
                  />
                  <div className="text-4xl font-bold text-white">NFCores</div>
                </Link>
              </div>

              {/* Información principal */}
              <div className="max-w-md mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">
                  Únete a la revolución NFC
                </h2>
                <p className="text-xl text-primary-100 leading-relaxed">
                  Crea tu cuenta y accede a nuestro ecosistema completo de productos NFC para emergencias, mascotas, negocios y más.
                </p>
              </div>

              {/* Carrusel de logos de submarcas */}
              <div className="w-full max-w-sm mb-12">
                <LogoCarousel autoPlay={true} interval={3500} />
              </div>

              {/* Beneficios */}
              <div className="grid grid-cols-2 gap-4 text-center max-w-sm">
                <div className="space-y-2">
                  <span className="text-2xl" role="img" aria-label="escudo">🛡️</span>
                  <p className="text-xs text-white font-medium">Seguridad total</p>
                </div>
                <div className="space-y-2">
                  <span className="text-2xl" role="img" aria-label="cohete">🚀</span>
                  <p className="text-xs text-white font-medium">Fácil configuración</p>
                </div>
                <div className="space-y-2">
                  <span className="text-2xl" role="img" aria-label="teléfono móvil">📱</span>
                  <p className="text-xs text-white font-medium">Multiplataforma</p>
                </div>
                <div className="space-y-2">
                  <span className="text-2xl" role="img" aria-label="flechas circulares">🔄</span>
                  <p className="text-xs text-white font-medium">Actualizaciones</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Formulario de registro */}
        <div className="w-full lg:w-1/2 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24">
          <div className="mx-auto w-full max-w-sm lg:w-96">
            {/* Logo para móviles */}
            <div className="lg:hidden text-center mb-8">
              <Link to="/" className="flex items-center justify-center space-x-3">
                <img
                  className="h-12 w-auto"
                  src="/logo.svg"
                  alt="NFCores Logo"
                  onError={(e) => {
                    e.currentTarget.src = '/assets/images/NFCores-original.png';
                  }}
                />
                <div className="text-3xl font-bold text-primary-600">NFCores</div>
              </Link>
            </div>

            {/* Título */}
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Crea tu cuenta
              </h2>
              <p className="text-gray-600">
                Accede a la plataforma para comprar tu pulsera SOS NFC y gestionar tus perfiles de emergencia
              </p>
            </div>
            
            {/* Información destacada */}
            <div className="bg-primary-50 border-l-4 border-primary-500 p-4 mb-6 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-primary-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-primary-800">
                    Al crear tu cuenta, tendrás acceso a todas las funcionalidades: comprar pulseras, seguir pedidos, administrar perfiles médicos y gestionar contactos de emergencia.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Formulario */}
            <RegisterForm />

            {/* Enlaces */}
            <div className="mt-6 text-center space-y-2">
              <p className="text-sm text-gray-600">
                ¿Ya tienes una cuenta?{' '}
                <Link to="/auth/login" className="font-medium text-primary-600 hover:text-primary-500">
                  Inicia sesión aquí
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
